// src/pages/Lobby.tsx

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import socket, {
  logoutSocket,
} from "../socket";

import "../components/Lobby.css";

// =====================================================
// CONFIG
// =====================================================

// =====================================================
// CONFIG
// =====================================================

import API_URL from "../config/api";

import {
  trackAnalyticsEvent,
  type FasoorGameMode,
} from "../services/analytics";


// =====================================================
// TYPES
// =====================================================

interface GameStartData {
  roomCode: string;
  hand?: unknown[];
  field?: unknown[];
  yourTurn?: boolean;
  yourScore?: number;
  opponentScore?: number;
  remainingDeck?: number;
  collected?: unknown[];
  opponentType?: "human" | "ai";
  profile?: {
    name?: string;
    avatar?: string;
  };
  opponentProfile?: {
    name?: string;
    avatar?: string;
  };
}

interface Player {
  id: number | string;
  fullName: string;
  username: string;
  avatar?: string | null;

  friendshipStatus:
    | "none"
    | "friends"
    | "request_sent"
    | "request_received"
    | "pending"
    | "declined"
    | "blocked";
}

interface Friend {
  friendshipId: number | string;
  id: number | string;
  fullName: string;
  username: string;
  avatar?: string | null;
}

interface CurrentProfile {
  id: number | string;
  full_name: string;
  username: string;
  email?: string;
  avatar: string | null;
  bio?: string | null;
  games_played?: number;
  wins?: number;
  losses?: number;
  draws?: number;
  rating?: number;
  win_rate?: number;
}

interface FriendRequest {
  requestId: number | string;
  createdAt: string;

  user: {
    id: number | string;
    fullName: string;
    username: string;
  };
}

interface GameInvitation {
  invitationId: string;
  fromUserId: number | string;
  fromUsername: string;
}

type LobbyMode =
  | "main"
  | "friend";

// =====================================================
// MINIMUM PLAYER AGE
// =====================================================

const MINIMUM_AGE = 18;

const getAge = (
  dob: string
): number | null => {

  if (
    !/^\d{2}\/\d{2}\/\d{4}$/.test(
      dob
    )
  ) {
    return null;
  }

  const [
    month,
    day,
    year,
  ] = dob
    .split("/")
    .map(Number);

  const birthDate =
    new Date(
      year,
      month - 1,
      day
    );

  if (
    birthDate.getFullYear() !==
      year ||
    birthDate.getMonth() !==
      month - 1 ||
    birthDate.getDate() !== day
  ) {
    return null;
  }

  const today =
    new Date();

  if (birthDate > today) {
    return null;
  }

  let age =
    today.getFullYear() -
    birthDate.getFullYear();

  const monthDifference =
    today.getMonth() -
    birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (
      monthDifference === 0 &&
      today.getDate() <
        birthDate.getDate()
    )
  ) {
    age--;
  }

  return age;
};

// =====================================================
// COMPONENT
// =====================================================

const Lobby: React.FC = () => {
  const navigate = useNavigate();

  // ===================================================
  // ANALYTICS GAME MODE
  // ===================================================
  //
  // We record a game only after the backend emits
  // "gameStart".
  //
  // This prevents cancelled matchmaking or declined
  // invitations from being counted as games.
  //
  // opponentType tells us AI vs human, while this ref
  // distinguishes friend games from online games.
  //

  const pendingGameModeRef =
    useRef<FasoorGameMode | null>(
      null
    );

  // ===================================================
  // GAME STATE
  // ===================================================

  const [
    onlinePlayers,
    setOnlinePlayers,
  ] = useState(0);

  const [
    statusMessage,
    setStatusMessage,
  ] = useState("");

  const [
    isSearching,
    setIsSearching,
  ] = useState(false);

  const [
    isStartingAI,
    setIsStartingAI,
  ] = useState(false);

  // ===================================================
  // GUEST AGE VERIFICATION
  // ===================================================
  //
  // Registered users already provided their date of
  // birth during registration.
  //
  // Guest DOB is used only for this local age check.
  // It is not saved to the P-ARENA database.
  //

  const [
    showGuestAgeGate,
    setShowGuestAgeGate,
  ] = useState(false);

  const [
    guestDateOfBirth,
    setGuestDateOfBirth,
  ] = useState("");

  const [
    guestAgeError,
    setGuestAgeError,
  ] = useState("");

  const [mode, setMode] =
    useState<LobbyMode>("main");

  // ===================================================
  // FRIEND STATE
  // ===================================================

  const [
    playerSearch,
    setPlayerSearch,
  ] = useState("");

  const [
    searchResults,
    setSearchResults,
  ] = useState<Player[]>([]);

  const [
    friends,
    setFriends,
  ] = useState<Friend[]>([]);

  const [
    currentProfile,
    setCurrentProfile,
  ] = useState<CurrentProfile | null>(
    null
  );

  // ===================================================
  // ONLINE FRIEND PRESENCE
  // ===================================================
  //
  // Contains authenticated user IDs currently connected
  // to P-ARENA.
  //
  // Example:
  // ["1", "2", "7"]
  //

  const [
    onlineUserIds,
    setOnlineUserIds,
  ] = useState<string[]>([]);

  const [
    friendRequests,
    setFriendRequests,
  ] = useState<FriendRequest[]>([]);

  const [
    searchingPlayers,
    setSearchingPlayers,
  ] = useState(false);

  const [
    loadingFriends,
    setLoadingFriends,
  ] = useState(false);

  const [
    friendActionId,
    setFriendActionId,
  ] = useState<
    number | string | null
  >(null);

  // ===================================================
  // GAME INVITATION STATE
  // ===================================================

  const [
    gameInvitation,
    setGameInvitation,
  ] =
    useState<GameInvitation | null>(
      null
    );

  const [
    sendingInviteId,
    setSendingInviteId,
  ] = useState<
    number | string | null
  >(null);

  const [
    acceptingInvitation,
    setAcceptingInvitation,
  ] = useState(false);

  // ===================================================
  // AUTH
  // ===================================================

  const isLoggedIn = () => {
    return Boolean(
      localStorage.getItem("token")
    );
  };

  const getToken = () => {
    return localStorage.getItem(
      "token"
    );
  };

  // ===================================================
  // LOGOUT / EXPIRED TOKEN HANDLER
  // ===================================================

  const handleUnauthorized =
    useCallback(() => {
      localStorage.removeItem(
        "token"
      );

      localStorage.removeItem(
        "user"
      );

      logoutSocket();

      navigate("/login", {
        state: {
          returnTo: "/lobby",
        },
      });
    }, [navigate]);

  // ===================================================
  // REQUIRE ACCOUNT
  // ===================================================

  const requireAccount = (
    action: () => void
  ) => {
    if (!isLoggedIn()) {
      navigate("/login", {
        state: {
          returnTo: "/lobby",
        },
      });

      return;
    }

    action();
  };

  // ===================================================
  // AUTHENTICATED API HELPER
  // ===================================================

  const authFetch = useCallback(
    async (
      endpoint: string,
      options: RequestInit = {}
    ) => {
      const token =
        localStorage.getItem(
          "token"
        );

      if (!token) {
        handleUnauthorized();

        throw new Error(
          "Authentication required."
        );
      }

      const response =
        await fetch(
          `${API_URL}${endpoint}`,
          {
            ...options,

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${token}`,

              ...(options.headers ||
                {}),
            },
          }
        );

      if (
        response.status === 401
      ) {
        handleUnauthorized();

        throw new Error(
          "Your session has expired."
        );
      }

      return response;
    },
    [handleUnauthorized]
  );

  // ===================================================
// LOAD CURRENT PROFILE
// ===================================================

const loadCurrentProfile =
  useCallback(async () => {
    if (!isLoggedIn()) {
      setCurrentProfile(null);
      return;
    }

    try {
      const response =
        await authFetch(
          "/api/profile/me"
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to load profile."
        );
      }

      setCurrentProfile(
        data.profile || null
      );

    } catch (error) {
      console.error(
        "Load profile error:",
        error
      );
    }
  }, [authFetch]);

  useEffect(() => {
    if (isLoggedIn()) {
      loadCurrentProfile();
    }
  }, [loadCurrentProfile]);

  // ===================================================
  // SOCKET EVENTS
  // ===================================================

  useEffect(() => {
    // -------------------------------------------------
    // GAME START
    // -------------------------------------------------

    const handleGameStart = (
      data: GameStartData
    ) => {
      console.log(
        "🎮 Game starting:",
        data
      );

      setGameInvitation(null);
      setSendingInviteId(null);
      setAcceptingInvitation(
        false
      );

      setIsSearching(false);
      setIsStartingAI(false);

      setStatusMessage("");

      // -----------------------------------------------
      // ANALYTICS — SUCCESSFUL GAME START
      // -----------------------------------------------

      let gameMode:
        FasoorGameMode | null =
          pendingGameModeRef.current;

      /*
        AI can be determined authoritatively from the
        gameStart payload even if the local pending mode
        was lost for some reason.
      */

      if (
        data.opponentType === "ai"
      ) {
        gameMode = "ai";
      }

      if (gameMode) {

        void trackAnalyticsEvent(
          "game_started",
          {
            gameName: "fasoor",
            gameMode,
            pagePath: "/game",
          }
        );

      } else {

        console.warn(
          "Unable to determine Fasoor game mode for analytics."
        );
      }

      pendingGameModeRef.current =
        null;

      navigate("/game", {
        state: data,
      });
    };

    // -------------------------------------------------
    // MATCHMAKING
    // -------------------------------------------------

    const handleMatchmakingWaiting = () => {
      console.log(
        "⏳ Waiting for online opponent..."
      );

      setIsSearching(true);

      setStatusMessage(
        "Searching for an online player..."
      );
    };

    const handleMatchmakingCancelled = () => {
      console.log(
        "❌ Matchmaking cancelled."
      );

      setIsSearching(false);

      setStatusMessage(
        "Matchmaking cancelled."
      );
    };

    // -------------------------------------------------
    // ONLINE MATCH FOUND
    // -------------------------------------------------

    const handleOnlineMatchFound = ({
      roomCode,
    }: {
      roomCode: string;
    }) => {
      console.log(
        "🎯 Online opponent found. Room:",
        roomCode
      );

      if (!roomCode) {
        setIsSearching(false);

        setStatusMessage(
          "Unable to start online game."
        );

        return;
      }

      setIsSearching(false);

      setStatusMessage(
        "Opponent found! Starting game..."
      );

      /*
      * Do NOT navigate here.
      *
      * The backend calls startRound(),
      * which emits "gameStart".
      *
      * handleGameStart() will receive
      * the complete game state and
      * navigate both players to /game.
      */
    };

    // -------------------------------------------------
    // MATCHMAKING ERROR
    // -------------------------------------------------

    const handleMatchmakingError = ({
      message,
    }: {
      message: string;
    }) => {
      console.error(
        "❌ Matchmaking error:",
        message
      );

      setIsSearching(false);

      setStatusMessage(
        message ||
          "Unable to find an online game."
      );
    };

    // -------------------------------------------------
    // ONLINE PLAYERS
    // -------------------------------------------------

    const handleOnlinePlayers = ({
      count,
    }: {
      count: number;
    }) => {
      setOnlinePlayers(
        Number(count) || 0
      );
    };

    // -------------------------------------------------
    // AUTHENTICATED ONLINE USERS
    // -------------------------------------------------

    const handleOnlineUsersChanged = ({
      onlineUserIds,
    }: {
      onlineUserIds: Array<
        number | string
      >;
    }) => {
      console.log(
        "🟢 Online users changed:",
        onlineUserIds
      );

      const normalizedIds =
        Array.isArray(onlineUserIds)
          ? onlineUserIds.map(
              (id) => String(id)
            )
          : [];

      setOnlineUserIds(
        normalizedIds
      );
    };

    // -------------------------------------------------
    // GENERIC SOCKET ERROR
    // -------------------------------------------------

    const handleError = (
      message: string
    ) => {
      console.error(
        "Socket error:",
        message
      );

      setIsSearching(false);
      setIsStartingAI(false);

      setSendingInviteId(null);
      setAcceptingInvitation(
        false
      );

      setStatusMessage(
        typeof message ===
          "string"
          ? message
          : "A game error occurred."
      );
    };

    // =================================================
    // FRIEND GAME INVITATION RECEIVED
    // =================================================

    const handleGameInvitation = (
      invitation: GameInvitation
    ) => {
      console.log(
        "💌 Game invitation received:",
        invitation
      );

      if (
        !invitation ||
        !invitation.invitationId
      ) {
        return;
      }

      setGameInvitation(
        invitation
      );

      setAcceptingInvitation(
        false
      );

      setStatusMessage(
        `${invitation.fromUsername} invited you to play Fasoor.`
      );
    };

    // =================================================
    // INVITATION SENT
    // =================================================

    const handleGameInvitationSent =
      ({
        friendId,
      }: {
        invitationId: string;
        friendId:
          | number
          | string;
      }) => {
        console.log(
          "💌 Game invitation sent:",
          friendId
        );

        setSendingInviteId(
          null
        );

        setStatusMessage(
          "Game invitation sent. Waiting for your friend to accept."
        );
      };

    // =================================================
    // INVITATION ERROR
    // =================================================

    const handleGameInvitationError =
      ({
        message,
      }: {
        message: string;
      }) => {
        console.error(
          "❌ Game invitation error:",
          message
        );

        setSendingInviteId(
          null
        );

        setAcceptingInvitation(
          false
        );

        setStatusMessage(
          message ||
            "Unable to process the game invitation."
        );
      };

    // =================================================
    // INVITATION DECLINED
    // =================================================

    const handleGameInvitationDeclined =
      () => {
        console.log(
          "❌ Game invitation declined."
        );

        setSendingInviteId(
          null
        );

        setStatusMessage(
          "Your friend declined the game invitation."
        );
      };

    // =================================================
    // INVITATION CANCELLED
    // =================================================

    const handleGameInvitationCancelled =
      ({
        invitationId,
      }: {
        invitationId: string;
      }) => {
        console.log(
          "⚠️ Game invitation cancelled:",
          invitationId
        );

        setGameInvitation(
          (current) => {
            if (
              current &&
              current.invitationId ===
                invitationId
            ) {
              return null;
            }

            return current;
          }
        );

        setAcceptingInvitation(
          false
        );

        setStatusMessage(
          "The game invitation is no longer available."
        );
      };

    // =================================================
    // INVITATION ACCEPTED BY FRIEND
    // =================================================

    const handleGameInvitationAccepted =
      ({
        roomCode,
      }: {
        invitationId: string;
        roomCode: string;
      }) => {
        console.log(
          "✅ Friend accepted invitation. Room:",
          roomCode
        );

        setSendingInviteId(
          null
        );

        setStatusMessage(
          "Your friend accepted. Starting game..."
        );

        /*
          We DO NOT navigate here.

          startRound() on the backend emits gameStart
          to both players.

          handleGameStart() above will navigate both
          browsers using the complete game state.
        */
      };

    // =================================================
    // REGISTER SOCKET EVENTS
    // =================================================

    socket.on(
      "gameStart",
      handleGameStart
    );

    socket.on(
      "matchmakingWaiting",
      handleMatchmakingWaiting
    );

    socket.on(
      "matchmakingCancelled",
      handleMatchmakingCancelled
    );

    socket.on(
      "onlinePlayers",
      handleOnlinePlayers
    );

    socket.on(
      "onlineUsersChanged",
      handleOnlineUsersChanged
    );

    socket.on(
      "error",
      handleError
    );

    socket.on(
      "gameInvitation",
      handleGameInvitation
    );

    socket.on(
      "gameInvitationSent",
      handleGameInvitationSent
    );

    socket.on(
      "gameInvitationError",
      handleGameInvitationError
    );

    socket.on(
      "gameInvitationDeclined",
      handleGameInvitationDeclined
    );

    socket.on(
      "gameInvitationCancelled",
      handleGameInvitationCancelled
    );

    socket.on(
      "gameInvitationAccepted",
      handleGameInvitationAccepted
    );

    // Ask backend for current socket count.
    socket.emit(
      "requestOnlinePlayers"
    );

    // Ask backend which authenticated users
    // are currently online.
    socket.emit(
      "requestOnlineUsers"
    );
    
    // =================================================
    // CLEANUP
    // =================================================

    return () => {
      socket.off(
        "gameStart",
        handleGameStart
      );

      socket.off(
        "matchmakingWaiting",
        handleMatchmakingWaiting
      );

      socket.off(
        "matchmakingCancelled",
        handleMatchmakingCancelled
      );

      socket.off(
        "onlinePlayers",
        handleOnlinePlayers
      );

      socket.off(
        "onlineUsersChanged",
        handleOnlineUsersChanged
      );

      socket.off(
        "error",
        handleError
      );

      socket.off(
        "gameInvitation",
        handleGameInvitation
      );

      socket.off(
        "gameInvitationSent",
        handleGameInvitationSent
      );

      socket.off(
        "gameInvitationError",
        handleGameInvitationError
      );

      socket.off(
        "gameInvitationDeclined",
        handleGameInvitationDeclined
      );

      socket.off(
        "gameInvitationCancelled",
        handleGameInvitationCancelled
      );

      socket.off(
        "gameInvitationAccepted",
        handleGameInvitationAccepted
      );
    };
  }, [navigate]);

  // ===================================================
  // LOAD FRIENDS
  // ===================================================

  const loadFriends =
    useCallback(async () => {
      if (!isLoggedIn()) {
        console.log(
          "👥 LOAD FRIENDS: User is not logged in."
        );

        setFriends([]);

        return;
      }

      try {
        setLoadingFriends(true);

        console.log(
          "👥 LOAD FRIENDS: Requesting /api/friends..."
        );

        const response =
          await authFetch(
            "/api/friends"
          );

        const data =
          await response.json();

        console.log(
          "👥 /api/friends RESPONSE:",
          data
        );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Unable to load friends."
          );
        }

        const loadedFriends =
          Array.isArray(data.friends)
            ? data.friends
            : [];

        console.log(
          "👥 FRIENDS BEING SAVED TO STATE:",
          loadedFriends
        );

        setFriends(
          loadedFriends
        );

      } catch (error) {
        console.error(
          "❌ Load friends error:",
          error
        );

        /*
          Do not clear the existing friends here.

          If the API temporarily fails, keeping the
          previous list is better than incorrectly
          showing "You haven't added any friends yet."
        */

        setStatusMessage(
          error instanceof Error
            ? error.message
            : "Unable to load friends."
        );

      } finally {
        setLoadingFriends(false);
      }
    }, [authFetch]);

  // ===================================================
  // LOAD FRIEND REQUESTS
  // ===================================================

  const loadFriendRequests =
    useCallback(async () => {
      if (!isLoggedIn()) {
        return;
      }

      try {
        const response =
          await authFetch(
            "/api/friends/requests"
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Unable to load friend requests."
          );
        }

        setFriendRequests(
          data.requests || []
        );

      } catch (error) {
        console.error(
          "Load requests error:",
          error
        );
      }
    }, [authFetch]);

  // ===================================================
  // LOAD FRIEND DATA WHEN FRIEND MODE OPENS
  // ===================================================

  useEffect(() => {
    if (
      mode === "friend" &&
      isLoggedIn()
    ) {
      loadFriends();
      loadFriendRequests();
    }
  }, [
    mode,
    loadFriends,
    loadFriendRequests,
  ]);

  // ===================================================
  // SEARCH PLAYERS
  // ===================================================

  const searchPlayers =
    async () => {
      const query =
        playerSearch.trim();

      if (query.length < 2) {
        setStatusMessage(
          "Enter at least 2 characters to search."
        );

        return;
      }

      try {
        setSearchingPlayers(
          true
        );

        setStatusMessage("");

        const response =
          await authFetch(
            `/api/friends/search?q=${encodeURIComponent(
              query
            )}`
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Unable to search players."
          );
        }

        setSearchResults(
          data.players || []
        );

        if (
          !data.players ||
          data.players.length === 0
        ) {
          setStatusMessage(
            "No players found."
          );
        }

      } catch (error) {
        console.error(
          "Player search error:",
          error
        );

        setStatusMessage(
          error instanceof Error
            ? error.message
            : "Unable to search players."
        );

      } finally {
        setSearchingPlayers(
          false
        );
      }
    };

  // ===================================================
  // SEND FRIEND REQUEST
  // ===================================================

  const sendFriendRequest =
    async (
      player: Player
    ) => {
      try {
        setFriendActionId(
          player.id
        );

        setStatusMessage("");

        const response =
          await authFetch(
            "/api/friends/request",
            {
              method: "POST",

              body:
                JSON.stringify({
                  userId:
                    Number(
                      player.id
                    ),
                }),
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Unable to send friend request."
          );
        }

        setSearchResults(
          (current) =>
            current.map(
              (item) =>
                String(
                  item.id
                ) ===
                String(
                  player.id
                )
                  ? {
                      ...item,

                      friendshipStatus:
                        "request_sent",
                    }
                  : item
            )
        );

        setStatusMessage(
          `Friend request sent to ${player.username}.`
        );

      } catch (error) {
        console.error(
          "Friend request error:",
          error
        );

        setStatusMessage(
          error instanceof Error
            ? error.message
            : "Unable to send friend request."
        );

      } finally {
        setFriendActionId(
          null
        );
      }
    };

  // ===================================================
  // ACCEPT FRIEND REQUEST
  // ===================================================

  const acceptFriendRequest =
    async (
      request: FriendRequest
    ) => {
      try {
        setFriendActionId(
          request.requestId
        );

        const response =
          await authFetch(
            `/api/friends/requests/${request.requestId}/accept`,
            {
              method: "POST",
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Unable to accept request."
          );
        }

        setFriendRequests(
          (current) =>
            current.filter(
              (item) =>
                String(
                  item.requestId
                ) !==
                String(
                  request.requestId
                )
            )
        );

        setStatusMessage(
          `${request.user.username} is now your friend.`
        );

        await loadFriends();

      } catch (error) {
        console.error(
          "Accept friend error:",
          error
        );

        setStatusMessage(
          error instanceof Error
            ? error.message
            : "Unable to accept friend request."
        );

      } finally {
        setFriendActionId(
          null
        );
      }
    };

  // ===================================================
  // DECLINE FRIEND REQUEST
  // ===================================================

  const declineFriendRequest =
    async (
      request: FriendRequest
    ) => {
      try {
        setFriendActionId(
          request.requestId
        );

        const response =
          await authFetch(
            `/api/friends/requests/${request.requestId}/decline`,
            {
              method: "POST",
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Unable to decline friend request."
          );
        }

        setFriendRequests(
          (current) =>
            current.filter(
              (item) =>
                String(
                  item.requestId
                ) !==
                String(
                  request.requestId
                )
            )
        );

        setStatusMessage(
          "Friend request declined."
        );

      } catch (error) {
        console.error(
          "Decline friend error:",
          error
        );

        setStatusMessage(
          error instanceof Error
            ? error.message
            : "Unable to decline friend request."
        );

      } finally {
        setFriendActionId(
          null
        );
      }
    };

  // ===================================================
  // REMOVE FRIEND
  // ===================================================

  const removeFriend =
    async (
      friend: Friend
    ) => {
      const confirmed =
        window.confirm(
          `Remove ${friend.username} from your friends?`
        );

      if (!confirmed) {
        return;
      }

      try {
        setFriendActionId(
          friend.id
        );

        const response =
          await authFetch(
            `/api/friends/${friend.id}`,
            {
              method: "DELETE",
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Unable to remove friend."
          );
        }

        setFriends(
          (current) =>
            current.filter(
              (item) =>
                String(
                  item.id
                ) !==
                String(
                  friend.id
                )
            )
        );

        setStatusMessage(
          `${friend.username} was removed from your friends.`
        );

      } catch (error) {
        console.error(
          "Remove friend error:",
          error
        );

        setStatusMessage(
          error instanceof Error
            ? error.message
            : "Unable to remove friend."
        );

      } finally {
        setFriendActionId(
          null
        );
      }
    };

  // ===================================================
  // CHECK IF FRIEND IS ONLINE
  // ===================================================

  const isFriendOnline = (
    friendId: number | string
  ): boolean => {
    return onlineUserIds.includes(
      String(friendId)
    );
  };
  
  // ===================================================
  // INVITE FRIEND TO PLAY
  // ===================================================

const inviteFriend = (
  friend: Friend
) => {
  console.log(
    "🔥 INVITE BUTTON CLICKED",
    {
      friend,
      socketConnected:
        socket.connected,
      socketId:
        socket.id,
      tokenExists:
        Boolean(
          localStorage.getItem(
            "token"
          )
        ),
    }
  );

  if (!isLoggedIn()) {
    console.log(
      "❌ No login token."
    );

    navigate("/login", {
      state: {
        returnTo: "/lobby",
      },
    });

    return;
  }

  if (!socket.connected) {
    console.log(
      "❌ Socket is NOT connected."
    );

    setStatusMessage(
      "Game server is not connected. Please refresh the page."
    );

    return;
  }

  console.log(
    "📤 EMITTING inviteFriend",
    {
      friendId: friend.id,
    }
  );

  setSendingInviteId(
    friend.id
  );

  setStatusMessage(
    `Sending game invitation to ${friend.username}...`
  );

  pendingGameModeRef.current =
    "friend";

  socket.emit(
    "inviteFriend",
    {
      friendId: friend.id,
    }
  );
};

  // ===================================================
  // ACCEPT GAME INVITATION
  // ===================================================

  const acceptGameInvitation =
    () => {
      if (
        !gameInvitation ||
        acceptingInvitation
      ) {
        return;
      }

      console.log(
        "✅ Accepting game invitation:",
        gameInvitation
      );

      setAcceptingInvitation(
        true
      );

      setStatusMessage(
        "Starting game..."
      );

      pendingGameModeRef.current =
        "friend";

      socket.emit(
        "acceptGameInvitation",
        {
          invitationId:
            gameInvitation
              .invitationId,
        }
      );
    };

  // ===================================================
  // DECLINE GAME INVITATION
  // ===================================================

  const declineGameInvitation =
    () => {
      if (!gameInvitation) {
        return;
      }

      console.log(
        "❌ Declining game invitation:",
        gameInvitation
      );

      socket.emit(
        "declineGameInvitation",
        {
          invitationId:
            gameInvitation
              .invitationId,
        }
      );

      setGameInvitation(null);

      pendingGameModeRef.current =
        null;

      setAcceptingInvitation(
        false
      );

      setStatusMessage(
        "Game invitation declined."
      );
    };

  // ===================================================
  // ONLINE MATCHMAKING
  // ===================================================

  const playOnline = () => {
    if (isSearching) {
      return;
    }

    if (!socket.connected) {
      setStatusMessage(
        "Game server is not connected. Please refresh the page."
      );

      return;
    }

    console.log(
      "🔎 Starting online matchmaking..."
    );

    pendingGameModeRef.current =
      "online";

    setIsSearching(true);

    setStatusMessage(
      "Searching for an online player..."
    );

    socket.emit(
      "findOnlineGame"
    );
  };

  // ===================================================
  // CANCEL MATCHMAKING
  // ===================================================

  const cancelMatchmaking = () => {
    console.log(
      "❌ Cancelling online matchmaking..."
    );

    socket.emit(
      "cancelOnlineGameSearch"
    );

    pendingGameModeRef.current =
      null;

    setIsSearching(false);

    setStatusMessage(
      "Matchmaking cancelled."
    );
  };

  // ===================================================
  // PLAY AGAINST AI
  // ===================================================

  // ===================================================
  // START AI GAME
  // ===================================================

  const startAIGame =
    () => {
      if (isStartingAI) {
        return;
      }

      setIsStartingAI(
        true
      );

      setStatusMessage(
        "Starting a game against the AI..."
      );

      pendingGameModeRef.current =
        "ai";

      socket.emit(
        "createAIGame",
        {
          difficulty:
            "medium",
        }
      );
    };

  // ===================================================
  // PLAY AGAINST AI
  // ===================================================
  //
  // Registered users already supplied DOB during
  // registration.
  //
  // Guests must pass the same 18+ age verification
  // used by the P-ARENA mobile app.
  // ===================================================

  const playAgainstAI =
    () => {
      if (isStartingAI) {
        return;
      }

      if (isLoggedIn()) {
        startAIGame();
        return;
      }

      setGuestDateOfBirth("");
      setGuestAgeError("");
      setShowGuestAgeGate(true);
    };

  // ===================================================
  // VERIFY GUEST AGE
  // ===================================================

  const verifyGuestAge =
    () => {
      const age =
        getAge(
          guestDateOfBirth.trim()
        );

      if (age === null) {
        setGuestAgeError(
          "Please enter a valid date of birth using MM/DD/YYYY."
        );

        return;
      }

      if (age < MINIMUM_AGE) {
        setGuestAgeError(
          "You must be at least 18 years old to play P-ARENA."
        );

        return;
      }

      setGuestAgeError("");
      setShowGuestAgeGate(false);

      startAIGame();
    };

  // ===================================================
  // CLOSE GUEST AGE GATE
  // ===================================================

  const closeGuestAgeGate =
    () => {
      setShowGuestAgeGate(false);
      setGuestDateOfBirth("");
      setGuestAgeError("");
    };

      // ===================================================
  // BACK TO GAME MODES
  // ===================================================

  const backToModes = () => {
    setMode("main");

    setPlayerSearch("");
    setSearchResults([]);

    setStatusMessage("");
  };

  // ===================================================
  // OPEN FRIEND MODE
  // ===================================================

  const openFriendMode =
    () => {
      requireAccount(() => {
        setMode(
          "friend"
        );

        setStatusMessage(
          ""
        );
      });
    };

  // ===================================================
  // LOGOUT
  // ===================================================

  const handleLogout = () => {
    if (isSearching) {
      socket.emit(
        "cancelMatchmaking"
      );
    }

    localStorage.removeItem(
      "token"
    );

    localStorage.removeItem(
      "user"
    );

    logoutSocket();

    setIsSearching(false);
    setIsStartingAI(false);

    setGameInvitation(null);
    setSendingInviteId(null);
    setAcceptingInvitation(
      false
    );

    setMode("main");

    setFriends([]);
    setFriendRequests([]);
    setSearchResults([]);

    setPlayerSearch("");

    setStatusMessage("");

    navigate("/");
  };

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <main className="lobby-page">

      {/* ============================================= */}
      {/* GUEST AGE VERIFICATION                        */}
      {/* ============================================= */}

      {showGuestAgeGate && (
        <div
          className="guest-age-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="guest-age-title"
        >
          <div className="guest-age-modal">

            <div className="guest-age-badge">
              18+
            </div>

            <h2 id="guest-age-title">
              Age Verification
            </h2>

            <p className="guest-age-description">
              You must be at least 18 years old to play P-ARENA.
              Please enter your date of birth.
            </p>

            <label
              className="guest-age-label"
              htmlFor="guest-date-of-birth"
            >
              DATE OF BIRTH
            </label>

            <input
              id="guest-date-of-birth"
              className="guest-age-input"
              type="text"
              inputMode="numeric"
              autoComplete="bday"
              placeholder="MM/DD/YYYY"
              maxLength={10}
              value={guestDateOfBirth}
              onChange={(event) => {
                const digits =
                  event.target.value
                    .replace(
                      /\D/g,
                      ""
                    )
                    .slice(
                      0,
                      8
                    );

                let formatted =
                  digits;

                if (
                  digits.length > 2
                ) {
                  formatted =
                    `${digits.slice(
                      0,
                      2
                    )}/${digits.slice(
                      2
                    )}`;
                }

                if (
                  digits.length > 4
                ) {
                  formatted =
                    `${digits.slice(
                      0,
                      2
                    )}/${digits.slice(
                      2,
                      4
                    )}/${digits.slice(
                      4
                    )}`;
                }

                setGuestDateOfBirth(
                  formatted
                );

                if (guestAgeError) {
                  setGuestAgeError(
                    ""
                  );
                }
              }}
              onKeyDown={(event) => {
                if (
                  event.key ===
                  "Enter"
                ) {
                  verifyGuestAge();
                }

                if (
                  event.key ===
                  "Escape"
                ) {
                  closeGuestAgeGate();
                }
              }}
              autoFocus
            />

            {guestAgeError && (
              <div
                className="guest-age-error"
                role="alert"
              >
                {guestAgeError}
              </div>
            )}

            <button
              type="button"
              className="guest-age-verify-button"
              onClick={
                verifyGuestAge
              }
            >
              VERIFY & PLAY
            </button>

            <button
              type="button"
              className="guest-age-cancel-button"
              onClick={
                closeGuestAgeGate
              }
            >
              CANCEL
            </button>

            <p className="guest-age-privacy">
              Your date of birth is used only for this age check
              and is not saved to your P-ARENA account.
            </p>

          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* GAME INVITATION MODAL                         */}
      {/* ============================================= */}

      {gameInvitation && (
        <div
          className="game-invitation-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="game-invitation-title"
        >

          <div className="game-invitation-modal">

            <div className="game-invitation-icon">
              ♠
            </div>

            <h2
              id="game-invitation-title"
            >
              Fasoor Invitation
            </h2>

            <p>
              <strong>
                {
                  gameInvitation
                    .fromUsername
                }
              </strong>{" "}
              invited you to play
              Fasoor.
            </p>

            <div className="game-invitation-actions">

              <button
                type="button"
                className="invitation-decline-button"
                disabled={
                  acceptingInvitation
                }
                onClick={
                  declineGameInvitation
                }
              >
                Decline
              </button>

              <button
                type="button"
                className="invitation-accept-button"
                disabled={
                  acceptingInvitation
                }
                onClick={
                  acceptGameInvitation
                }
              >
                {acceptingInvitation
                  ? "Starting Fasoor..."
                  : "Play Fasoor"}
              </button>

            </div>

          </div>

        </div>
      )}

      {/* ============================================= */}
      {/* TOP NAVIGATION                                */}
      {/* ============================================= */}

      <button
        type="button"
        className="lobby-back-home"
        onClick={() =>
          navigate("/")
        }
      >
        ← Game House
      </button>

      {/* ============================================= */}
      {/* LOGGED-IN USER                                */}
      {/* ============================================= */}

      {isLoggedIn() && (
        <div className="lobby-user-area">

          <div className="lobby-user-wrapper">

            <button
              type="button"
              className="lobby-user-info"
              onClick={() =>
                navigate("/profile")
              }
              title="View Profile"
            >

              <img
                src={
                  currentProfile?.avatar
                    ? currentProfile.avatar.startsWith(
                        "/uploads/"
                      )
                      ? `${API_URL}${currentProfile.avatar}`
                      : currentProfile.avatar
                    : "/avatars/default_avatar.png"
                }
                alt={
                  currentProfile?.username ||
                  "Profile"
                }
                className="lobby-user-avatar"
                onError={(event) => {
                  event.currentTarget.onerror =
                    null;

                  event.currentTarget.src =
                    "/avatars/default_avatar.png";
                }}
              />

              <div className="lobby-user-details">
                <span className="lobby-user-name">
                  {currentProfile?.username ||
                    "Player"}
                </span>

                {currentProfile?.rating !==
                  undefined && (
                  <span className="lobby-user-rating">
                    ★ {currentProfile.rating}
                  </span>
                )}
              </div>

            </button>

          </div>

          <button
            type="button"
            className="logout-button"
            onClick={handleLogout}
          >
            Logout
          </button>

        </div>
      )}

      <section className="lobby-card">

        {/* =========================================== */}
        {/* HEADER                                      */}
        {/* =========================================== */}

        <header className="lobby-header">

          <div className="lobby-logo">
            ♠ P-ARENA
          </div>

          {mode === "friend" && (
            <>
              <h1>
                Play With a Friend
              </h1>

              <p>
                Find friends and invite
                them to play P-ARENA.
              </p>
            </>
          )}

        </header>

        {/* =========================================== */}
        {/* MAIN MODE                                   */}
        {/* =========================================== */}

        {mode === "main" && (
          <div className="play-selector">

            <div className="play-selector-heading">

              <span className="play-selector-kicker">
                CHOOSE YOUR MATCH
              </span>

              <h2>
                Select Game Mode
              </h2>

              <p>
                How would you like
                to play P-ARENA?
              </p>

            </div>

            <div className="play-options">

              {/* AI */}

              <button
                type="button"
                className="play-option"
                onClick={
                  playAgainstAI
                }
                disabled={
                  isStartingAI ||
                  isSearching
                }
              >

                <div className="play-option-icon">
                  🤖
                </div>

                <div className="play-option-info">

                  <strong>
                    {isStartingAI
                      ? "Starting Game..."
                      : "Play Against AI"}
                  </strong>

                  <span>
                    Jump straight
                    into a game
                  </span>

                </div>

                <div className="play-option-meta">

                  <span className="free-label">
                    INSTANT
                  </span>

                  <span className="play-arrow">
                    ›
                  </span>

                </div>

              </button>

              {/* FRIEND */}

              <button
                type="button"
                className="play-option"
                onClick={
                  openFriendMode
                }
                disabled={
                  isStartingAI ||
                  isSearching
                }
              >

                <div className="play-option-icon">
                  👥
                </div>

                <div className="play-option-info">

                  <strong>
                    Play With a Friend
                  </strong>

                  <span>
                    See your friends and
                    invite them to play
                  </span>

                </div>

                <div className="play-option-meta">

                  {!isLoggedIn() && (
                    <span className="lock-label">
                      🔒 ACCOUNT
                    </span>
                  )}

                  <span className="play-arrow">
                    ›
                  </span>

                </div>

              </button>

              {/* ONLINE */}

              <button
                type="button"
                className="play-option"
                onClick={() =>
                  requireAccount(
                    playOnline
                  )
                }
                disabled={
                  isStartingAI ||
                  isSearching
                }
              >

                <div className="play-option-icon">
                  🌐
                </div>

                <div className="play-option-info">

                  <strong>
                    {isSearching
                      ? "Finding Opponent..."
                      : "Find Online Player"}
                  </strong>

                  <span>
                    Match with another
                    player online
                  </span>

                </div>

                <div className="play-option-meta">

                  {!isLoggedIn() ? (
                    <span className="lock-label">
                      🔒 ACCOUNT
                    </span>
                  ) : (
                    <span className="online-label">
                      ● ONLINE
                    </span>
                  )}

                  <span className="play-arrow">
                    ›
                  </span>

                </div>

              </button>

            </div>

            <div className="play-selector-footer">

              <span className="online-dot" />

              <span>
                {onlinePlayers} player
                {onlinePlayers === 1
                  ? ""
                  : "s"}{" "}
                online
              </span>

            </div>

          </div>
        )}

        {/* =========================================== */}
        {/* MATCHMAKING                                 */}
        {/* =========================================== */}

        {mode === "main" &&
          isSearching && (
            <div className="matchmaking-panel">

              <div className="matchmaking-loader" />

              <strong>
                Looking for an
                opponent...
              </strong>

              <span>
                We'll start
                automatically when
                another player is
                found.
              </span>

              <button
                type="button"
                className="lobby-button cancel-button"
                onClick={
                  cancelMatchmaking
                }
              >
                Cancel Search
              </button>

            </div>
          )}

        {/* =========================================== */}
        {/* FRIEND MODE                                 */}
        {/* =========================================== */}

        {mode === "friend" && (
          <section className="private-room-section">

            <button
              type="button"
              className="friend-back-button"
              onClick={
                backToModes
              }
            >
              ← Game Modes
            </button>

            {/* ======================================= */}
            {/* SEARCH PLAYERS                          */}
            {/* ======================================= */}

            <div className="friend-option">

              <div className="friend-option-icon">
                🔎
              </div>

              <div className="friend-option-content">

                <h3>
                  Find Players
                </h3>

                <p>
                  Search registered
                  P-ARENA players by
                  username or name.
                </p>

                <div className="friend-search-form">

                  <input
                    type="text"
                    className="friend-search-input"
                    placeholder="Search username or name"
                    value={
                      playerSearch
                    }
                    disabled={
                      searchingPlayers
                    }
                    onChange={(
                      event
                    ) => {
                      setPlayerSearch(
                        event.target
                          .value
                      );

                      setSearchResults(
                        []
                      );
                    }}
                    onKeyDown={(
                      event
                    ) => {
                      if (
                        event.key ===
                        "Enter"
                      ) {
                        searchPlayers();
                      }
                    }}
                  />

                  <button
                    type="button"
                    className="lobby-button friend-search-button"
                    disabled={
                      searchingPlayers ||
                      playerSearch
                        .trim()
                        .length < 2
                    }
                    onClick={
                      searchPlayers
                    }
                  >
                    {searchingPlayers
                      ? "Searching..."
                      : "Search"}
                  </button>

                </div>

                {/* SEARCH RESULTS */}

                {searchResults.length >
                  0 && (
                  <div className="player-search-results">

                    {searchResults.map(
                      (player) => (
                        <div
                          className="player-result-card"
                          key={
                            player.id
                          }
                        >

                          <div className="player-avatar-wrapper">

                            <img
                              src={
                                player.avatar
                                  ? player.avatar.startsWith(
                                      "/uploads/"
                                    )
                                    ? `${API_URL}${player.avatar}`
                                    : player.avatar
                                  : "/avatars/default_avatar.png"
                              }
                              alt={player.username}
                              className="player-avatar-image"
                              onError={(event) => {
                                event.currentTarget.onerror =
                                  null;

                                event.currentTarget.src =
                                  "/avatars/default_avatar.png";
                              }}
                            />

                          </div>

                          <div className="player-result-info">

                            <strong>
                              {
                                player.username
                              }
                            </strong>

                            <span>
                              {
                                player.fullName
                              }
                            </span>

                          </div>

                          <div className="player-result-action">

                            {player.friendshipStatus ===
                              "none" && (
                              <button
                                type="button"
                                className="small-action-button add-friend-button"
                                disabled={
                                  friendActionId ===
                                  player.id
                                }
                                onClick={() =>
                                  sendFriendRequest(
                                    player
                                  )
                                }
                              >
                                {friendActionId ===
                                player.id
                                  ? "Sending..."
                                  : "+ Add Friend"}
                              </button>
                            )}

                            {player.friendshipStatus ===
                              "request_sent" && (
                              <span className="friend-state sent-state">
                                ✓ Request
                                Sent
                              </span>
                            )}

                            {player.friendshipStatus ===
                              "request_received" && (
                              <span className="friend-state received-state">
                                Request
                                Received
                              </span>
                            )}

                            {player.friendshipStatus ===
                              "friends" && (
                              <span className="friend-state friends-state">
                                ✓ Friends
                              </span>
                            )}

                          </div>

                        </div>
                      )
                    )}

                  </div>
                )}

              </div>

            </div>

            {/* ======================================= */}
            {/* FRIEND REQUESTS                         */}
            {/* ======================================= */}

            {friendRequests.length >
              0 && (
              <div className="friend-option">

                <div className="friend-option-icon">
                  🔔
                </div>

                <div className="friend-option-content">

                  <h3>
                    Friend Requests
                  </h3>

                  <p>
                    Players who want
                    to connect with
                    you.
                  </p>

                  <div className="friend-request-list">

                    {friendRequests.map(
                      (request) => (
                        <div
                          className="friend-request-card"
                          key={
                            request.requestId
                          }
                        >

                          <div className="player-avatar">
                            {request.user.username
                              .charAt(
                                0
                              )
                              .toUpperCase()}
                          </div>

                          <div className="player-result-info">

                            <strong>
                              {
                                request
                                  .user
                                  .username
                              }
                            </strong>

                            <span>
                              {
                                request
                                  .user
                                  .fullName
                              }
                            </span>

                          </div>

                          <div className="request-actions">

                            <button
                              type="button"
                              className="small-action-button decline-request-button"
                              disabled={
                                friendActionId ===
                                request.requestId
                              }
                              onClick={() =>
                                declineFriendRequest(
                                  request
                                )
                              }
                            >
                              Decline
                            </button>

                            <button
                              type="button"
                              className="small-action-button accept-request-button"
                              disabled={
                                friendActionId ===
                                request.requestId
                              }
                              onClick={() =>
                                acceptFriendRequest(
                                  request
                                )
                              }
                            >
                              Accept
                            </button>

                          </div>

                        </div>
                      )
                    )}

                  </div>

                </div>

              </div>
            )}

            {/* ======================================= */}
            {/* MY FRIENDS                              */}
            {/* ======================================= */}

            <div className="friend-option">

              <div className="friend-option-icon">
                👥
              </div>

              <div className="friend-option-content">

                <h3>
                  My Friends
                </h3>

                <p>
                  Choose an online friend
                  and invite them to play.
                </p>

                {loadingFriends ? (
                  <div className="friends-empty">
                    Loading friends...
                  </div>
                ) : friends.length === 0 ? (
                  <div className="friends-empty">
                    You haven't added
                    any friends yet.
                  </div>
                ) : (
                  <div className="friends-list">

                    {friends.map((friend) => {
                      const online =
                        isFriendOnline(
                          friend.id
                        );

                      return (
                        <div
                          className={`friend-card ${
                            online
                              ? "friend-online"
                              : "friend-offline"
                          }`}
                          key={
                            friend.id
                          }
                        >

                          {/* AVATAR */}

                          <div className="friend-avatar-wrapper">

                            <img
                              src={
                                friend.avatar
                                  ? friend.avatar.startsWith("/uploads/")
                                    ? `${API_URL}${friend.avatar}`
                                    : friend.avatar
                                  : "/avatars/default_avatar.png"
                              }
                              alt={friend.username}
                              className="friend-avatar-image"
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src =
                                  "/avatars/default_avatar.png";
                              }}
                            />

                            <span
                              className={`friend-presence-dot ${
                                online ? "online" : "offline"
                              }`}
                              title={
                                online
                                  ? "Online"
                                  : "Offline"
                              }
                            />

                          </div>

                          {/* FRIEND INFO */}

                          <div className="player-result-info">

                            <strong>
                              {
                                friend.username
                              }
                            </strong>

                            <span>
                              {
                                friend.fullName
                              }
                            </span>

                            <span
                              className={`friend-presence-text ${
                                online
                                  ? "online"
                                  : "offline"
                              }`}
                            >
                              {online
                                ? "● Online"
                                : "● Offline"}
                            </span>

                          </div>

                          {/* ACTIONS */}

                          <div className="friend-card-actions">

                            {online ? (
                              <button
                                type="button"
                                className="small-action-button invite-friend-button"
                                disabled={
                                  sendingInviteId !==
                                  null
                                }
                                onClick={() =>
                                  inviteFriend(
                                    friend
                                  )
                                }
                              >
                                {String(
                                  sendingInviteId
                                ) ===
                                String(
                                  friend.id
                                )
                                  ? "Sending..."
                                  : "♠ Play"}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="small-action-button friend-offline-button"
                                disabled
                              >
                                Offline
                              </button>
                            )}

                            <button
                              type="button"
                              className="remove-friend-button"
                              disabled={
                                String(
                                  friendActionId
                                ) ===
                                String(
                                  friend.id
                                )
                              }
                              onClick={() =>
                                removeFriend(
                                  friend
                                )
                              }
                              aria-label={`Remove ${friend.username}`}
                              title="Remove friend"
                            >
                              ×
                            </button>

                          </div>

                        </div>
                      );
                    })}

                  </div>
                )}

              </div>

            </div>

          </section>
        )}

        {/* =========================================== */}
        {/* STATUS                                      */}
        {/* =========================================== */}

        {statusMessage && (
          <div
            className="lobby-status"
            role="status"
          >
            {statusMessage}
          </div>
        )}

      </section>

    </main>
  );
};

export default Lobby;